import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { validateNewPassword, checkPasswordRequirements } from '../../utils/validation';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const VisibilityIcon = MdVisibility as any;
const VisibilityOffIcon = MdVisibilityOff as any;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initializeFromStoredTokens } = useAuth();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Compute password requirements status
  const passwordRequirements = useMemo(() => checkPasswordRequirements(newPassword), [newPassword]);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    } else {
      setToken(resetToken);
    }
  }, [searchParams]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);

    // Validate if confirm password is also filled
    if (confirmPassword) {
      const validation = validateNewPassword(value, confirmPassword);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);

    // Validate if password is also filled
    if (newPassword) {
      const validation = validateNewPassword(newPassword, value);
      setPasswordError(validation.isValid ? '' : validation.error || '');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setIsLoading(true);

    // Validate password and confirm password
    const validation = validateNewPassword(newPassword, confirmPassword);
    if (!validation.isValid) {
      setPasswordError(validation.error || 'Invalid password');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiRequest('/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      // Store the tokens returned after successful password reset
      if (response.access_token && response.refresh_token) {
        const tokenManager = (await import('../../utils/api')).TokenManager.getInstance();
        tokenManager.storeTokens(response.access_token, response.refresh_token);

        // Initialize auth context with the new tokens
        try {
          await initializeFromStoredTokens();
        } catch (initError) {
          console.error('Failed to initialize auth after password reset:', initError);
          setError('Password reset successful but failed to initialize session. Please log in manually.');
          setIsLoading(false);
          return;
        }
      }

      // Redirect to dashboard after successful reset
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);

      // Check if error is about token expiry
      if (errorMessage.toLowerCase().includes('expired')) {
        setIsTokenExpired(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendReset = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      await apiRequest('/resend-password-reset', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      setResendSuccess(true);
      setIsTokenExpired(false);
    } catch (error: any) {
      setError(error.message || 'Failed to send new reset link. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (error && !token) {
    // Invalid token - show error state
    return (
      <div className="relative flex min-h-screen flex-col bg-background">
        <div className="flex h-full grow flex-col">
          <Header variant="auth" />

          <div className="px-40 flex flex-1 justify-center py-5">
            <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
              <div className="text-center py-8">
                <div className="mb-6">
                  <svg className="w-20 h-20 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 pb-3">
                  Invalid Reset Link
                </h2>
                <p className="text-red-400 text-base px-4 pb-6">
                  {error}
                </p>
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="inline-block px-6 py-2 bg-primary text-white rounded font-medium hover:bg-primary/90 transition-colors"
                >
                  Request New Reset Link
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div className="flex h-full grow flex-col">
        <Header variant="auth" />

        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="flex flex-col w-[512px] max-w-[512px] py-5 max-w-[960px] flex-1">
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight px-4 text-center pb-3 pt-5">
              Create new password
            </h2>
            <p className="text-text-secondary text-base px-4 text-center pb-6">
              Enter your new password below.
            </p>
            {resendSuccess && (
              <div className="mx-4 mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded text-green-300 text-sm">
                A new password reset link has been sent to your email. Please check your inbox.
              </div>
            )}
            {error && (
              <div className="mx-4 mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm">
                {error}
                {isTokenExpired && (
                  <div className="mt-3">
                    <button
                      onClick={handleResendReset}
                      disabled={resendLoading}
                      className="px-4 py-2 bg-primary text-white rounded font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resendLoading ? 'Sending...' : 'Send New Reset Link'}
                    </button>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col items-center w-full">
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">New Password</p>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter your new password"
                      required
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] pr-12 text-base font-normal leading-normal"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors"
                    >
                      {showPassword ? (
                        <VisibilityIcon className="w-5 h-5" />
                      ) : (
                        <VisibilityOffIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </label>
              </div>
              <div className="flex max-w-[480px] w-full px-4 pb-3">
                <div className="text-xs w-full">
                  <p className={`mb-2 ${newPassword.length === 0 ? 'text-text-secondary' : 'text-white'}`}>Password must:</p>
                  <ul className="space-y-1">
                    <li className={`flex items-center gap-2 ${
                      newPassword.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.minLength
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {newPassword.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.minLength ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Be at least 8 characters long</span>
                    </li>
                    <li className={`flex items-center gap-2 ${
                      newPassword.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.hasNumber
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {newPassword.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.hasNumber ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Contain at least one number</span>
                    </li>
                    <li className={`flex items-center gap-2 ${
                      newPassword.length === 0
                        ? 'text-text-secondary'
                        : passwordRequirements.hasSpecialChar
                          ? 'text-green-500'
                          : 'text-red-400'
                    }`}>
                      {newPassword.length === 0 ? (
                        <span className="w-4 h-4" />
                      ) : passwordRequirements.hasSpecialChar ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>Contain at least one special character</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex max-w-[480px] w-full flex-wrap items-end gap-4 px-4 py-3 justify-center">
                <label className="flex flex-col min-w-40 flex-1 max-w-[480px] w-full">
                  <p className="text-white text-base font-medium leading-normal pb-2">Confirm New Password</p>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="Confirm your new password"
                      required
                      className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-14 placeholder:text-text-secondary p-[15px] pr-12 text-base font-normal leading-normal"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? (
                        <VisibilityIcon className="w-5 h-5" />
                      ) : (
                        <VisibilityOffIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </label>
              </div>
              {passwordError && (
                <div className="mx-4 mb-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-yellow-300 text-xs max-w-[480px] w-full">
                  {passwordError}
                </div>
              )}
              <div className="flex px-4 py-3 w-full justify-center">
                <button
                  type="submit"
                  disabled={isLoading || !token}
                  className="flex max-w-[450px] w-full cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-5 bg-primary text-white text-base font-bold leading-normal tracking-[0.015em] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="truncate">
                    {isLoading ? 'Resetting password...' : 'Reset Password'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
